import arrowUrl from '@/static/assets/car/driving/arrow.svg';
import { Box, themedColor } from '@lemonade-hq/cdk';
import { rgba } from 'polished';
/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css, type DefaultTheme } from 'styled-components';
import { GameOnTitle, GreenText, PrimaryText, Shoutout, ShoutoutContainer, Subtitle } from './Common';
import { Emoji } from './cheerups';
import { CHEER_UP_TIMEOUT } from './useDriving';

const ExplanationContainer = styled.div`
  transition: opacity 0.3s ease;
`;

const ArrowButton = styled.img.attrs({
  src: arrowUrl.src,
})<{ isActive: boolean }>`
  padding: 20px;
  border: 1px solid ${themedColor('glass')};
  border-radius: 3px;
  background-color: ${themedColor('white')};
  transition: background-color 0.3s ease;

  ${({ isActive, theme }: { isActive: boolean; theme: DefaultTheme }) =>
    isActive &&
    css`
      background-color: ${rgba(theme.colors.primary, 0.1)};
    `}
`;

const ArrowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${ArrowButton}:not(:first-of-type) {
    margin-left: 25px;
  }
`;

const GameModeTitle: React.FC<{
  isGameOn: boolean;
  isGameEnded: boolean;
  isScreenCountingDown: boolean;
  isLeftKeyDown: boolean;
  isRightKeyDown: boolean;
  isUpKeyDown: boolean;
  isScreenCountdownZero: boolean;
  gameTimerState: 'high' | 'low' | 'zero';
  onReplay: () => void;
  activeCheerUps: React.ReactNode[];
}> = ({
  isGameOn,
  isGameEnded,
  isScreenCountingDown,
  isScreenCountdownZero,
  isLeftKeyDown,
  isRightKeyDown,
  isUpKeyDown,
  gameTimerState,
  onReplay,
  activeCheerUps,
}) => {
  return (
    <>
      <CSSTransition in={isScreenCountingDown} unmountOnExit mountOnEnter timeout={500}>
        <ShoutoutContainer>
          <GameOnTitle>
            {isScreenCountdownZero ? (
              <>
                <Emoji>🏁</Emoji>
                <span> GO </span>
                <Emoji>🏁</Emoji>
              </>
            ) : (
              <i>GAME MODE ACTIVATED</i>
            )}
          </GameOnTitle>
          <ExplanationContainer style={{ opacity: !isScreenCountdownZero ? 1 : 0 }}>
            <Subtitle style={{ marginBottom: '40px', lineHeight: '32px' }}>
              <Box>
                <span>Use the arrows to catch </span>
                <PrimaryText> pink dots</PrimaryText>
                <span>!</span>
              </Box>
              <Box>
                <span>Catch</span>
                <GreenText> green dots </GreenText>
                <span>to charge your car’s battery for more time.</span>
              </Box>
            </Subtitle>
            <ArrowContainer>
              <ArrowButton isActive={isLeftKeyDown} />
              <ArrowButton isActive={isUpKeyDown} style={{ transform: 'rotate(90deg)' }} />
              <ArrowButton isActive={isRightKeyDown} style={{ transform: 'rotate(180deg)' }} />
            </ArrowContainer>
          </ExplanationContainer>
        </ShoutoutContainer>
      </CSSTransition>
      <CSSTransition
        in={isGameOn && !isScreenCountingDown && gameTimerState !== 'high'}
        unmountOnExit
        mountOnEnter
        timeout={500}
      >
        <ShoutoutContainer>
          <GameOnTitle>
            {isGameEnded ? (
              <>
                <span>Awesome job! </span>
                <Emoji>🏆</Emoji>
              </>
            ) : gameTimerState === 'zero' ? (
              <>
                <Emoji>⏱</Emoji>
                <span> GAME OVER</span>
              </>
            ) : (
              'HURRY UP!'
            )}
          </GameOnTitle>
          <Subtitle
            style={{
              cursor: isGameEnded ? 'pointer' : 'default',
              opacity: isGameEnded ? 1 : 0,
              pointerEvents: isGameEnded ? 'all' : 'none',
            }}
          >
            <PrimaryText onClick={onReplay}>Wanna go again?</PrimaryText>
          </Subtitle>
        </ShoutoutContainer>
      </CSSTransition>
      <ShoutoutContainer
        style={{
          opacity: gameTimerState === 'high' && !isScreenCountingDown ? 1 : 0,
        }}
      >
        <TransitionGroup>
          <CSSTransition key={activeCheerUps[0]?.toString()} unmountOnExit mountOnEnter timeout={CHEER_UP_TIMEOUT}>
            <Shoutout>{activeCheerUps[0]}</Shoutout>
          </CSSTransition>
        </TransitionGroup>
      </ShoutoutContainer>
    </>
  );
};

export default React.memo(GameModeTitle);
